import { toPng } from 'html-to-image';
import { Download } from 'lucide-react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';

import { useHandbook } from './HandbookContext';
import { handbookPdf } from './handbookPdf';
import type { DiagramKind } from './handbookTypes';

export function HandbookPdfButton({ automatic = false }: { automatic?: boolean }) {
  const handbook = useHandbook();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  const download = useCallback(async () => {
    if (started.current) return;
    started.current = true;
    setBusy(true);
    setFailed(false);
    try {
      await document.fonts.ready;
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      const diagrams: Partial<Record<DiagramKind, string>> = {};
      for (const figure of document.querySelectorAll<HTMLElement>('[data-handbook-diagram]')) {
        const kind = figure.dataset.handbookDiagram as DiagramKind;
        if (!diagrams[kind]) diagrams[kind] = await toPng(figure, { pixelRatio: 2, backgroundColor: '#ffffff' });
      }
      const blob = await new Promise<Blob>((resolve) => pdfMake.createPdf(handbookPdf(handbook, diagrams), undefined, undefined, pdfFonts).getBlob(resolve));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'RapidSOS-Studio-Handbook.pdf';
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setFailed(true);
    } finally {
      started.current = false;
      setBusy(false);
    }
  }, [handbook]);

  useEffect(() => {
    if (automatic) void download();
  }, [automatic, download]);

  return (
    <div className="ml-auto flex items-center gap-3">
      {failed ? <p role="alert" className="text-sm text-destructive">The PDF could not be created. Please try again.</p> : null}
      <Button type="button" size="sm" disabled={busy} aria-busy={busy} onClick={() => void download()}>
        <Download className="mr-1.5 h-3.5 w-3.5" />
        {busy ? 'Preparing PDF…' : 'Download PDF'}
      </Button>
    </div>
  );
}
